import { useMemo, useState } from 'react';
import { TECHNIQUES, type Technique } from '../data/techniques';
import { Badge, Card } from '../ui/components';
import type { Tier } from '../engine/types';

const STATUS_META: Record<
  Technique['status'],
  { label: string; tone: 'ok' | 'denied' | 'error' | 'unavailable' | 'accent'; blurb: string }
> = {
  implemented: {
    label: 'Implemented',
    tone: 'ok',
    blurb: 'Ships as a live probe in this repo and produces a real measured value.',
  },
  partial: {
    label: 'Partial',
    tone: 'denied',
    blurb: 'Some of the signal is obtainable here; the rest needs infrastructure this static site does not have.',
  },
  'not-measurable': {
    label: 'Not measurable here',
    tone: 'unavailable',
    blurb: 'Requires raw wire-level or server-side visibility. Catalogued with the reason and what it would take.',
  },
  planned: {
    label: 'Planned',
    tone: 'accent',
    blurb: 'Designed and specified, deferred to a later phase.',
  },
};

const TIER_META: Record<Tier, string> = {
  client: 'client-side',
  edge: 'edge (Worker)',
  server: 'server-side',
};

function TechniqueCard({ t }: { t: Technique }) {
  const meta = STATUS_META[t.status];
  return (
    <details className="rounded-xl border border-white/6 bg-black/20">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2.5 px-4 py-3">
        <Badge tone={meta.tone}>{meta.label}</Badge>
        <span className="text-sm font-medium text-zinc-100">{t.title}</span>
        <span className="ml-auto flex items-center gap-2 text-xs text-zinc-500">
          <span className="rounded border border-white/8 px-1.5 py-0.5">{t.section}</span>
          <span className="capitalize">{TIER_META[t.tier]}</span>
        </span>
      </summary>
      <div className="space-y-3 px-4 pb-4 text-sm leading-6 text-zinc-400">
        <p className="text-xs text-zinc-500">{meta.blurb}</p>
        <p>{t.summary}</p>
        {t.limitation && (
          <div className="rounded-lg border border-amber-500/15 bg-amber-500/5 p-3">
            <div className="mb-1 text-xs uppercase tracking-wider text-amber-300/80">
              Limitation
            </div>
            <p className="text-amber-100/80">{t.limitation}</p>
          </div>
        )}
        {t.probe && (
          <p className="text-xs text-zinc-500">
            Implemented by the <code className="font-mono text-zinc-400">{t.probe}</code> probe.
          </p>
        )}
        <div>
          <div className="mb-1 text-xs uppercase tracking-wider text-zinc-600">Sources</div>
          <ul className="space-y-0.5 text-xs">
            {/* Dedupe: two FLD keys can resolve to the same URL, which would
                otherwise produce a duplicate React key and a console error. */}
            {[...new Set(t.sources)].map((s) => (
              <li key={s}>
                <a
                  href={s}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-orange-300 underline underline-offset-2 hover:text-orange-200"
                >
                  {s}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}

export function Techniques() {
  const [statusFilter, setStatusFilter] = useState<Technique['status'] | 'all'>('all');
  const [docFilter, setDocFilter] = useState<Technique['doc'] | 'all'>('all');

  const filtered = useMemo(
    () =>
      TECHNIQUES.filter(
        (t) =>
          (statusFilter === 'all' || t.status === statusFilter) &&
          (docFilter === 'all' || t.doc === docFilter),
      ),
    [statusFilter, docFilter],
  );

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: TECHNIQUES.length };
    for (const t of TECHNIQUES) c[t.status] = (c[t.status] ?? 0) + 1;
    return c;
  }, []);

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h1 className="text-3xl font-semibold tracking-tight text-white">Technique coverage catalog</h1>
        <p className="max-w-4xl text-sm leading-6 text-zinc-400">
          Every technique from the two source research documents, each with an honest
          measurability verdict. A static page behind an edge genuinely cannot see some of these —
          those are catalogued with the reason and the infrastructure they would need, rather than
          shown as if they were collected. This page is what makes the coverage claim auditable.
        </p>
      </div>

      <Card title="Coverage summary">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CoverageStat label="Implemented" value={counts['implemented'] ?? 0} tone="ok" />
          <CoverageStat label="Partial" value={counts['partial'] ?? 0} tone="denied" />
          <CoverageStat
            label="Not measurable here"
            value={counts['not-measurable'] ?? 0}
            tone="unavailable"
          />
          <CoverageStat label="Planned" value={counts['planned'] ?? 0} tone="accent" />
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Status</span>
          {(['all', 'implemented', 'partial', 'not-measurable', 'planned'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`rounded-lg border px-2.5 py-1 text-xs transition-colors ${
                statusFilter === s
                  ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                  : 'border-white/8 text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {s === 'all' ? `all (${counts.all})` : `${s} (${counts[s] ?? 0})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Source</span>
          <button
            type="button"
            onClick={() => setDocFilter('all')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'all'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            both
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('fingerprinting')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'fingerprinting'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            fingerprinting
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('bot-detection')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'bot-detection'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            bot-detection
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('desync-cache')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'desync-cache'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            desync-cache
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('security-research')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'security-research'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            security-research
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('creepjs')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'creepjs'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            creepjs
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('papers')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'papers'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            papers
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('baseline')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'baseline'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            baseline
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('dns-cache')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'dns-cache'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            dns-cache
          </button>
          <button
            type="button"
            onClick={() => setDocFilter('cert-store')}
            className={`rounded-lg border px-2.5 py-1 text-xs ${
              docFilter === 'cert-store'
                ? 'border-orange-500/40 bg-orange-500/15 text-orange-200'
                : 'border-white/8 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            cert-store
          </button>
        </div>
      </div>

      <Card subtitle={`${filtered.length} technique(s) shown.`}>
        <div className="space-y-2">
          {filtered.map((t) => (
            <TechniqueCard key={t.id} t={t} />
          ))}
        </div>
      </Card>
    </div>
  );
}

function CoverageStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'denied' | 'unavailable' | 'accent';
}) {
  return (
    <div className="rounded-xl border border-white/6 bg-black/20 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-zinc-400">{label}</span>
        <Badge tone={tone}>{value}</Badge>
      </div>
    </div>
  );
}
